import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Node,
  Edge,
  NodeProps,
  Handle,
  Position,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Connector {
  id: string;
  name: string;
  category: string;
  description: string;
  color: string;
  icon?: React.ReactNode;
  configKey?: string;
  envVars?: string[];
  actions?: { id: string; description: string; inputs: string[]; outputs: string[] }[];
  endpoints?: { method: string; path: string; description: string }[];
  usedIn?: string[];
}

const CATEGORY_COLORS: Record<string, string> = {
  Auth: '#e53e3e',
  'Source Control': '#3182ce',
  'Issue Tracking': '#0052CC',
  Docs: '#2c7a7b',
  Automation: '#dd6b20',
  Infrastructure: '#6b46c1',
  AI: '#d69e2e',
  'CI/CD': '#276749',
  Workflow: '#97266d',
};

// Real logo URLs per connector id
const CONNECTOR_LOGOS: Record<string, string> = {
  okta: 'https://cdn.simpleicons.org/okta/007DC1',
  bitbucket: 'https://cdn.simpleicons.org/bitbucket/0052CC',
  jira: 'https://cdn.simpleicons.org/jira/0052CC',
  confluence: 'https://cdn.simpleicons.org/confluence/172B4D',
  awx: 'https://cdn.simpleicons.org/ansible/EE0000',
  n8n: 'https://cdn.simpleicons.org/n8n/EA4B71',
  skynet: 'https://cdn.simpleicons.org/terraform/6B46C1',
  gocd: 'https://cdn.simpleicons.org/gocd/94399E',
  'aws-bedrock': 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg',
  postgres: 'https://cdn.simpleicons.org/postgresql/336791',
};

// Hub node dimensions — must match the rendered Box exactly so ReactFlow
// handle math (which uses these to find the node center/border) is correct.
const HUB_W = 180;
const HUB_H = 180;
// Connector node dimensions
const NODE_W = 280;
const NODE_H = 130;

// ── Hub node (center) ─────────────────────────────────────────────────────────

const HubNode: React.FC<NodeProps> = () => (
  <>
    {/* Single centered handle — edges will originate from the true center */}
    <Handle
      type="source"
      position={Position.Top}
      style={{ opacity: 0, top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}
    />
    <Box sx={{
      width: HUB_W,
      height: HUB_H,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
      border: '3px solid rgba(255,255,255,0.18)',
      boxShadow: '0 0 0 10px rgba(255,255,255,0.03), 0 0 50px rgba(255,255,255,0.07)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.5,
    }}>
      <Typography sx={{ fontSize: '2.6rem', lineHeight: 1 }}>🏪</Typography>
      <Typography sx={{
        fontSize: '0.72rem', fontWeight: 800, color: 'white',
        textAlign: 'center', letterSpacing: '0.08em',
        textTransform: 'uppercase', lineHeight: 1.3, px: 1,
      }}>
        COE<br />Marketplace
      </Typography>
    </Box>
  </>
);

// ── Connector node ────────────────────────────────────────────────────────────

const ConnectorNode: React.FC<NodeProps> = ({ data }) => {
  const { connector, selected, dimmed, isDark, onSelect } = data as {
    connector: Connector;
    selected: boolean;
    dimmed: boolean;
    isDark: boolean;
    onSelect: (c: Connector) => void;
  };
  const color = CATEGORY_COLORS[connector.category] ?? '#4a5568';
  const logoUrl = CONNECTOR_LOGOS[connector.id];

  const nodeBg = selected
    ? `linear-gradient(135deg, ${color}28 0%, ${color}12 100%)`
    : dimmed
    ? isDark ? 'rgba(20,22,32,0.5)' : 'rgba(240,244,248,0.5)'
    : isDark ? 'rgba(26,30,44,0.92)' : 'rgba(255,255,255,0.95)';
  const nodeBorder = selected ? color : dimmed
    ? isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'
    : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)';
  const nameColor = selected ? color : isDark ? 'rgba(255,255,255,0.92)' : '#1a202c';
  const logoBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)';
  const logoBorder = isDark ? (selected ? color + '60' : 'rgba(255,255,255,0.1)') : (selected ? color + '60' : 'rgba(0,0,0,0.1)');

  return (
    <>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Box
        onClick={() => onSelect(connector)}
        sx={{
          width: NODE_W,
          borderRadius: '10px',
          background: nodeBg,
          border: `1.5px solid ${nodeBorder}`,
          boxShadow: selected
            ? `0 0 0 3px ${color}40, 0 8px 32px ${color}30`
            : dimmed ? 'none' : isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          opacity: dimmed ? 0.25 : 1,
          transform: selected ? 'scale(1.07)' : 'scale(1)',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Top accent bar */}
        <Box sx={{ height: 2.5, background: `linear-gradient(90deg, ${color}, ${color}50)` }} />

        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Logo */}
          <Box sx={{
            width: 56, height: 56, borderRadius: '12px', flexShrink: 0,
            bgcolor: logoBg,
            border: `1px solid ${logoBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={connector.name}
                style={{ width: 36, height: 36, objectFit: 'contain' }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <Typography sx={{ fontSize: '1.6rem' }}>🔌</Typography>
            )}
          </Box>

          {/* Text */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{
              fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.3,
              color: nameColor,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {connector.name}
            </Typography>
            <Box sx={{
              mt: 0.5,
              display: 'inline-block',
              fontSize: '0.68rem', fontWeight: 700, color,
              bgcolor: `${color}18`,
              border: `1px solid ${color}35`,
              px: 1, py: 0.25, borderRadius: '4px',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {connector.category}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const nodeTypes = { hub: HubNode, connector: ConnectorNode };

// ── Detail Panel ──────────────────────────────────────────────────────────────

const DetailPanel: React.FC<{ connector: Connector; onClose: () => void }> = ({ connector, onClose }) => {
  const color = CATEGORY_COLORS[connector.category] ?? '#4a5568';
  const logoUrl = CONNECTOR_LOGOS[connector.id];

  return (
    <Box sx={{
      position: 'absolute', top: 16, right: 16, width: 320, zIndex: 10,
      bgcolor: '#1a1f2e', borderRadius: '16px',
      border: `1px solid ${color}40`,
      boxShadow: `0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px ${color}20`,
      overflow: 'hidden',
      animation: 'slideIn 0.2s ease',
      '@keyframes slideIn': {
        from: { opacity: 0, transform: 'translateX(20px)' },
        to: { opacity: 1, transform: 'translateX(0)' },
      },
    }}>
      <Box sx={{ height: 3, background: `linear-gradient(90deg, ${color}, ${color}60)` }} />

      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '8px',
                bgcolor: 'rgba(255,255,255,0.07)',
                border: `1px solid ${color}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', flexShrink: 0,
              }}>
                {logoUrl ? (
                  <img src={logoUrl} alt={connector.name} style={{ width: 24, height: 24, objectFit: 'contain' }} />
                ) : (
                  <Typography sx={{ fontSize: '1.1rem' }}>🔌</Typography>
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'white', lineHeight: 1.2 }}>{connector.name}</Typography>
                <Chip label={connector.category} size="small" sx={{
                  height: 16, fontSize: '0.58rem', fontWeight: 700, mt: 0.3,
                  bgcolor: `${color}20`, color, border: `1px solid ${color}40`,
                }} />
              </Box>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'white' }, mt: -0.5 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{
        px: 2.5, py: 2, maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: `${color}40`, borderRadius: 2 },
      }}>
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
          {connector.description}
        </Typography>

        {connector.configKey && (
          <Box>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Config Key</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: 'rgba(0,0,0,0.4)', color: '#68d391', px: 1.5, py: 0.75, borderRadius: '6px', border: '1px solid rgba(104,211,145,0.2)', wordBreak: 'break-all' }}>
              {connector.configKey}
            </Box>
          </Box>
        )}

        {connector.envVars && connector.envVars.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Env Variables</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {connector.envVars.map(v => (
                <Box key={v} sx={{ fontFamily: 'monospace', fontSize: '0.65rem', bgcolor: 'rgba(0,0,0,0.4)', color: '#fbd38d', px: 1, py: 0.3, borderRadius: '4px', border: '1px solid rgba(251,211,141,0.2)', wordBreak: 'break-all' }}>{v}</Box>
              ))}
            </Box>
          </Box>
        )}

        {connector.usedIn && connector.usedIn.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Used In</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {connector.usedIn.map(u => (
                <Chip key={u} label={u} size="small" sx={{ height: 20, fontSize: '0.62rem', bgcolor: `${color}15`, color, border: `1px solid ${color}30` }} />
              ))}
            </Box>
          </Box>
        )}

        {connector.actions && connector.actions.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>Scaffolder Actions</Typography>
            {connector.actions.map(action => (
              <Box key={action.id} sx={{ bgcolor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', p: 1.5, mb: 1 }}>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 700, color, bgcolor: `${color}15`, px: 1, py: 0.2, borderRadius: '4px', display: 'inline-block', mb: 0.5, wordBreak: 'break-all' }}>{action.id}</Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mb: 0.75 }}>{action.description}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: 'rgba(255,255,255,0.2)', mb: 0.3 }}>INPUTS</Typography>
                    {action.inputs.map(i => <Typography key={i} sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', wordBreak: 'break-word' }}>→ {i}</Typography>)}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: 'rgba(255,255,255,0.2)', mb: 0.3 }}>OUTPUTS</Typography>
                    {action.outputs.map(o => <Typography key={o} sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', wordBreak: 'break-word' }}>← {o}</Typography>)}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {connector.endpoints && connector.endpoints.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>API Endpoints</Typography>
            {connector.endpoints.map((ep, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', bgcolor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', p: 1, mb: 0.75 }}>
                <Box sx={{
                  fontSize: '0.58rem', fontWeight: 700, fontFamily: 'monospace', px: 0.8, py: 0.2, borderRadius: '4px', flexShrink: 0,
                  bgcolor: ep.method === 'GET' ? 'rgba(56,161,105,0.2)' : ep.method === 'POST' ? 'rgba(221,107,32,0.2)' : 'rgba(128,90,213,0.2)',
                  color: ep.method === 'GET' ? '#68d391' : ep.method === 'POST' ? '#f6ad55' : '#b794f4',
                  border: `1px solid ${ep.method === 'GET' ? 'rgba(104,211,145,0.3)' : ep.method === 'POST' ? 'rgba(246,173,85,0.3)' : 'rgba(183,148,244,0.3)'}`,
                }}>{ep.method}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, wordBreak: 'break-all' }}>{ep.path}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.35)' }}>{ep.description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Build hub-and-spoke graph ─────────────────────────────────────────────────
// Hub center is at (0,0). Node positions are offset so their visual center
// lands exactly on the circle, not their top-left corner.

function buildHubSpoke(
  connectors: Connector[],
  onSelect: (c: Connector) => void,
  isDark: boolean,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const RADIUS = 420;
  const total = connectors.length;

  // Hub: position so its center is at (0,0)
  nodes.push({
    id: 'hub',
    type: 'hub',
    position: { x: -HUB_W / 2, y: -HUB_H / 2 },
    // Declare exact size so ReactFlow computes handle positions correctly
    width: HUB_W,
    height: HUB_H,
    data: {},
    draggable: false,
  });

  connectors.forEach((c, i) => {
    const angle = (2 * Math.PI * i) / total - Math.PI / 2;
    // Center of connector node on the circle
    const cx = RADIUS * Math.cos(angle);
    const cy = RADIUS * Math.sin(angle);
    // Top-left position (ReactFlow uses top-left origin)
    const x = cx - NODE_W / 2;
    const y = cy - NODE_H / 2;

    const color = CATEGORY_COLORS[c.category] ?? '#4a5568';

    nodes.push({
      id: c.id,
      type: 'connector',
      position: { x, y },
      width: NODE_W,
      height: NODE_H,
      data: { connector: c, selected: false, dimmed: false, isDark, onSelect },
      draggable: true,
    });

    edges.push({
      id: `hub-${c.id}`,
      source: 'hub',
      target: c.id,
      animated: false,
      style: { stroke: `${color}60`, strokeWidth: 1, transition: 'all 0.25s ease' },
      type: 'straight',
    });
  });

  return { nodes, edges };
}

// ── Inner diagram ─────────────────────────────────────────────────────────────

const DiagramInner: React.FC<{ connectors: Connector[]; diagramTheme: 'dark' | 'light' }> = ({ connectors, diagramTheme }) => {
  const isDark = diagramTheme === 'dark';
  const [selected, setSelected] = useState<Connector | null>(null);
  const { fitView } = useReactFlow();

  const handleSelect = useCallback((c: Connector) => {
    setSelected(prev => prev?.id === c.id ? null : c);
  }, []);

  const { nodes: initNodes, edges: initEdges } = useMemo(
    () => buildHubSpoke(connectors, handleSelect, isDark),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectors, handleSelect],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);

  // Sync selection into node/edge data without rebuilding positions
  useEffect(() => {
    setNodes(ns => ns.map(n => {
      if (n.type !== 'connector') return n;
      const c = n.data.connector as Connector;
      return {
        ...n,
        data: {
          ...n.data,
          selected: selected?.id === c.id,
          dimmed: selected !== null && selected.id !== c.id,
          isDark,
        },
      };
    }));
    setEdges(es => es.map(e => {
      const c = connectors.find(x => x.id === e.target);
      if (!c) return e;
      const color = CATEGORY_COLORS[c.category] ?? '#4a5568';
      const isSelected = selected?.id === c.id;
      const isDimmed = selected !== null && !isSelected;
      return {
        ...e,
        animated: isSelected,
        style: {
          stroke: isSelected ? color : isDimmed ? 'rgba(255,255,255,0.04)' : `${color}60`,
          strokeWidth: isSelected ? 2.5 : 1,
          transition: 'all 0.25s ease',
        },
      };
    }));
  }, [selected, connectors, setNodes, setEdges]);

  useEffect(() => {
    setTimeout(() => fitView({ padding: 0.15 }), 60);
  }, [fitView]);

  // Sync isDark into all connector nodes when theme changes
  useEffect(() => {
    setNodes(ns => ns.map(n => {
      if (n.type !== 'connector') return n;
      return { ...n, data: { ...n.data, isDark } };
    }));
    setEdges(es => es.map(e => {
      const c = connectors.find(x => x.id === e.target);
      if (!c) return e;
      const color = CATEGORY_COLORS[c.category] ?? '#4a5568';
      return {
        ...e,
        style: {
          ...e.style,
          stroke: isDark ? `${color}60` : `${color}80`,
        },
      };
    }));
  }, [isDark, connectors, setNodes, setEdges]);

  const panelBg = isDark ? 'rgba(26,31,46,0.92)' : 'rgba(255,255,255,0.92)';
  const panelBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const labelColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)';
  const textColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)';
  const dividerColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const hintColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.35)';
  const statColors = isDark
    ? ['#90cdf4', '#fbd38d', '#68d391']
    : ['#2563eb', '#d97706', '#16a34a'];

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onPaneClick={() => setSelected(null)}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.15}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={28}
          size={1}
          color={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'}
        />
        <Controls
          showInteractive={false}
          style={{
            background: isDark ? 'rgba(26,31,46,0.9)' : 'rgba(255,255,255,0.9)',
            border: `1px solid ${panelBorder}`,
            borderRadius: 8,
          }}
        />

        {/* Legend */}
        <Box sx={{
          position: 'absolute', bottom: 16, right: 16, zIndex: 5,
          bgcolor: panelBg, border: `1px solid ${panelBorder}`,
          borderRadius: '12px', p: 1.5, backdropFilter: 'blur(12px)',
        }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: labelColor, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Categories</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
              <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.65rem', color: textColor }}>{cat}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${dividerColor}` }}>
            <Typography sx={{ fontSize: '0.6rem', color: hintColor }}>Click a node · Drag to reposition · Scroll to zoom</Typography>
          </Box>
        </Box>

        {/* Stats */}
        <Box sx={{
          position: 'absolute', top: 16, left: 16, zIndex: 5,
          bgcolor: panelBg, border: `1px solid ${panelBorder}`,
          borderRadius: '12px', px: 2, py: 1.25, backdropFilter: 'blur(12px)',
          display: 'flex', gap: 2.5, alignItems: 'center',
        }}>
          {[
            { value: connectors.length, label: 'Connectors', color: statColors[0] },
            { value: Object.keys(CATEGORY_COLORS).length, label: 'Categories', color: statColors[1] },
            { value: new Set(connectors.flatMap(c => c.usedIn ?? [])).size, label: 'Features', color: statColors[2] },
          ].map((stat, i, arr) => (
            <React.Fragment key={stat.label}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: '0.58rem', color: labelColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</Typography>
              </Box>
              {i < arr.length - 1 && <Box sx={{ width: 1, height: 28, bgcolor: dividerColor }} />}
            </React.Fragment>
          ))}
        </Box>
      </ReactFlow>

      {selected && <DetailPanel connector={selected} onClose={() => setSelected(null)} />}
    </Box>
  );
};

// ── Public export ─────────────────────────────────────────────────────────────

interface ConnectorDiagramProps {
  connectors: Connector[];
  diagramTheme?: 'dark' | 'light';
}

export const ConnectorDiagram: React.FC<ConnectorDiagramProps> = ({ connectors, diagramTheme = 'light' }) => (
  <Box sx={{ position: 'absolute', inset: 0, bgcolor: diagramTheme === 'dark' ? '#0d1117' : '#f0f4f8' }}>
    <ReactFlowProvider>
      <DiagramInner connectors={connectors} diagramTheme={diagramTheme} />
    </ReactFlowProvider>
  </Box>
);
